import React, { useState } from 'react';
import { ArrowLeft, Copy, Check, ShieldCheck, Mail, Lock, Database, Trash2, Smartphone, FileText } from 'lucide-react';

export const QuickQuoteTerms: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const supportEmail = "hi@yata.dev";

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(supportEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div className="min-h-screen bg-[#07090E] text-[#E2E8F0] font-sans selection:bg-[#10B981] selection:text-[#07090E] relative overflow-x-hidden pb-20">
      {/* Background Grid Lines */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.03] z-0"
        style={{
          backgroundImage: `
            linear-gradient(to right, #10B981 1px, transparent 1px),
            linear-gradient(to bottom, #10B981 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}
      />

      {/* Radial Glows */}
      <div className="fixed top-0 right-0 w-[500px] h-[500px] bg-[#10B981]/[0.04] rounded-full blur-[140px] pointer-events-none z-0" />
      <div className="fixed bottom-0 left-0 w-[500px] h-[500px] bg-[#00F0FF]/[0.03] rounded-full blur-[160px] pointer-events-none z-0" />

      {/* Header Bar */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#07090E]/80 border-b border-[#10B981]/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between font-mono text-xs">
          <a
            href="/"
            className="inline-flex items-center space-x-2 text-gray-400 hover:text-[#10B981] transition-colors"
          >
            <ArrowLeft size={16} />
            <span>VOLTAR PARA YATA.DEV</span>
          </a>

          <div className="flex items-center space-x-2 text-[#10B981] font-bold">
            <ShieldCheck size={16} />
            <span className="tracking-wider">QUICKQUOTE // LEGAL</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 space-y-10">

        {/* Hero Title Block */}
        <div className="border border-[#10B981]/30 bg-[#0C111A] p-6 sm:p-10 relative font-mono space-y-6">
          <div className="flex items-center space-x-4">
            {/* QuickQuote SVG Logo */}
            <div className="w-14 h-14 sm:w-16 sm:h-16 shrink-0 rounded-2xl bg-gradient-to-br from-[#10B981] to-[#059669] p-3 shadow-lg shadow-[#10B981]/20 flex items-center justify-center">
              <svg viewBox="0 0 512 512" className="w-full h-full text-white fill-current">
                <rect x="56" y="40" width="400" height="432" rx="52" fill="none" stroke="currentColor" strokeWidth="24" />
                <rect x="118" y="140" width="180" height="24" rx="12" />
                <rect x="118" y="196" width="220" height="24" rx="12" />
                <rect x="118" y="252" width="160" height="24" rx="12" />
                <circle cx="360" cy="365" r="40" fill="#F59E0B" />
                <text x="360" y="378" textAnchor="middle" fontSize="42" fontWeight="800" fill="#07090E">$</text>
              </svg>
            </div>

            <div>
              <h1 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                Política de Privacidade e Exclusão de Dados
              </h1>
              <p className="text-xs text-gray-400 mt-1">
                QuickQuote &nbsp;·&nbsp; Última atualização: <span className="text-[#10B981]">6 de agosto de 2026</span>
              </p>
            </div>
          </div>

          <p className="font-sans text-sm sm:text-base text-gray-300 leading-relaxed border-t border-gray-800 pt-4">
            O <strong>QuickQuote</strong> ("nós", "nosso") respeita a sua privacidade e está comprometido em proteger os dados pessoais de nossos usuários ("você"). Esta Política de Privacidade descreve como suas informações são coletadas, usadas, armazenadas e como você pode solicitar a exclusão definitiva de seus dados.
          </p>
        </div>

        {/* Section 1: Coleta */}
        <section className="bg-[#0A0D15] border border-gray-800 p-6 sm:p-8 space-y-6">
          <div className="flex items-center space-x-3 text-[#10B981]">
            <Database size={20} />
            <h2 className="text-lg sm:text-xl font-bold font-mono text-white">1. Informações que Coletamos</h2>
          </div>

          <p className="text-sm text-gray-300 leading-relaxed font-sans">
            Para fornecer os serviços de criação, gestão e sincronização de orçamentos e recibos profissionais em PDF, o QuickQuote coleta e processa as seguintes informações:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-mono">
            <div className="bg-[#07090E] p-4 border border-gray-800 space-y-2">
              <div className="text-[#10B981] font-bold">// DADOS DE CONTA E AUTENTICAÇÃO</div>
              <p className="text-gray-300 font-sans leading-relaxed">
                E-mail e identificadores de conta (via cadastro por e-mail/senha ou provedores OAuth seguros como Google e Apple) para acesso protegido.
              </p>
            </div>

            <div className="bg-[#07090E] p-4 border border-gray-800 space-y-2">
              <div className="text-[#10B981] font-bold">// PERFIL E DADOS DA EMPRESA</div>
              <p className="text-gray-300 font-sans leading-relaxed">
                Nome do usuário, nome da empresa, documento (CPF ou CNPJ), telefone e logomarca fornecida para personalização dos documentos PDF.
              </p>
            </div>

            <div className="bg-[#07090E] p-4 border border-gray-800 space-y-2">
              <div className="text-[#10B981] font-bold">// ORÇAMENTOS E CLIENTES</div>
              <p className="text-gray-300 font-sans leading-relaxed">
                Nomes de clientes, descrições de produtos/serviços, valores financeiros e histórico de cotações geradas.
              </p>
            </div>

            <div className="bg-[#07090E] p-4 border border-gray-800 space-y-2">
              <div className="text-[#10B981] font-bold">// PERMISSÕES DO DISPOSITIVO</div>
              <p className="text-gray-300 font-sans leading-relaxed">
                <strong>Galeria de Fotos:</strong> Opcional para selecionar a logo da empresa.<br/>
                <strong>Contatos:</strong> Opcional para preenchimento rápido do cliente.
              </p>
            </div>
          </div>
        </section>

        {/* Section 2: Uso */}
        <section className="bg-[#0A0D15] border border-gray-800 p-6 sm:p-8 space-y-4">
          <div className="flex items-center space-x-3 text-[#10B981]">
            <FileText size={20} />
            <h2 className="text-lg sm:text-xl font-bold font-mono text-white">2. Como Usamos suas Informações</h2>
          </div>

          <ul className="space-y-2 text-sm text-gray-300 font-sans leading-relaxed list-disc list-inside">
            <li>Autenticar o acesso do usuário à plataforma com segurança.</li>
            <li>Gerar, visualizar e exportar orçamentos e recibos no formato PDF.</li>
            <li>Armazenar e sincronizar os dados na nuvem utilizando os serviços do **Firebase (Google Cloud Platform)**.</li>
            <li>Gerenciar as cotas de utilização de documentos do aplicativo.</li>
          </ul>

          <div className="bg-[#10B981]/10 border border-[#10B981]/30 p-4 text-xs font-mono text-[#10B981]">
            ✓ COMPROMISSO DE PRIVACIDADE: Nós não vendemos, alugamos ou compartilhamos seus dados pessoais com terceiros para fins de marketing ou publicidade.
          </div>
        </section>

        {/* Section 3: Segurança */}
        <section className="bg-[#0A0D15] border border-gray-800 p-6 sm:p-8 space-y-4">
          <div className="flex items-center space-x-3 text-[#10B981]">
            <Lock size={20} />
            <h2 className="text-lg sm:text-xl font-bold font-mono text-white">3. Armazenamento, Criptografia e Segurança</h2>
          </div>

          <p className="text-sm text-gray-300 leading-relaxed font-sans">
            Todas as informações transmitidas entre o aplicativo QuickQuote e os nossos servidores de nuvem utilizam conexões estritamente criptografadas via protocolo <strong>HTTPS / TLS</strong>. Adotamos práticas rígidas de segurança no Firebase (Google Cloud) para proteger seus dados contra acesso não autorizado, alteração ou vazamento.
          </p>
        </section>

        {/* Section 4: Exclusão de Conta (CRÍTICO PARA LOJAS) */}
        <section id="data-deletion" className="bg-[#0C111A] border-2 border-[#10B981] p-6 sm:p-8 space-y-6 relative">
          <div className="absolute top-0 right-0 bg-[#10B981] text-[#07090E] font-mono text-[10px] font-bold px-3 py-1 uppercase tracking-widest">
            Exigência Apple & Google
          </div>

          <div className="flex items-center space-x-3 text-[#10B981]">
            <Trash2 size={24} />
            <h2 className="text-xl sm:text-2xl font-bold font-mono text-white">
              4. Instruções para Solicitação de Exclusão de Conta e Dados
            </h2>
          </div>

          <p className="text-sm text-gray-300 leading-relaxed font-sans">
            Você tem o direito de solicitar a **exclusão definitiva** da sua conta e de todos os seus dados armazenados em nossos servidores a qualquer momento.
          </p>

          <div className="bg-[#07090E] p-6 border border-[#10B981]/40 space-y-4 font-mono text-xs">
            <div className="text-white font-bold text-sm text-[#10B981]">// PASSO A PASSO PARA SOLICITAR A EXCLUSÃO</div>

            <ol className="space-y-3 text-gray-300 font-sans leading-relaxed list-decimal list-inside">
              <li>
                Envie um e-mail para o suporte em: <strong className="text-[#10B981]">hi@yata.dev</strong>
              </li>
              <li>
                No assunto do e-mail, digite exatamente: <br />
                <code className="bg-[#10B981]/10 text-[#10B981] px-2 py-1 border border-[#10B981]/30 rounded inline-block mt-1">
                  Solicitação de Exclusão de Conta - QuickQuote
                </code>
              </li>
              <li>
                No corpo do e-mail, informe o <strong>endereço de e-mail cadastrado</strong> na conta que deseja excluir.
              </li>
            </ol>

            <div className="border-t border-gray-800 pt-4 space-y-2">
              <div className="text-gray-400 font-bold">// O QUE É EXCLUÍDO PERMANENTEMENTE:</div>
              <p className="text-gray-300 font-sans text-xs leading-relaxed">
                Após a confirmação da solicitação, sua conta será encerrada e todos os dados associados (perfil da empresa, histórico de orçamentos sincronizados no Firebase Firestore e registros de autenticação) serão <strong>permanentemente excluídos de nossos bancos de dados em até 30 dias</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* Section 5: Contato */}
        <section className="bg-[#0A0D15] border border-gray-800 p-6 sm:p-8 space-y-6">
          <div className="flex items-center space-x-3 text-[#10B981]">
            <Mail size={20} />
            <h2 className="text-lg sm:text-xl font-bold font-mono text-white">5. Contato e Suporte</h2>
          </div>

          <p className="text-sm text-gray-300 leading-relaxed font-sans">
            Se você tiver dúvidas sobre esta Política de Privacidade ou sobre o tratamento dos seus dados pessoais, entre em contato diretamente com o responsável técnico:
          </p>

          <div className="bg-[#07090E] p-6 border border-gray-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 font-mono text-xs">
            <div>
              <div className="text-white font-bold text-sm">Luan Lima</div>
              <div className="text-gray-400 text-xs">Desenvolvedor & Responsável Técnico // QuickQuote</div>
              <div className="text-[#10B981] text-xs mt-1">hi@yata.dev</div>
            </div>

            <button
              onClick={handleCopyEmail}
              className="inline-flex items-center space-x-2 bg-[#10B981] text-[#07090E] font-bold px-4 py-2.5 hover:bg-[#34D399] transition-all"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              <span>{copied ? 'E-MAIL COPIADO!' : 'COPIAR E-MAIL DE SUPORTE'}</span>
            </button>
          </div>
        </section>

        {/* Footer */}
        <footer className="pt-8 border-t border-gray-800 text-center font-mono text-xs text-gray-500 space-y-2">
          <p>© 2026 QuickQuote — Luan Lima. Todos os direitos reservados.</p>
          <p>Hospedado em <a href="https://yata.dev" className="text-[#10B981] hover:underline">yata.dev</a></p>
        </footer>

      </main>
    </div>
  );
};
